import { FlagTwoTone, MessageTwoTone } from "@ant-design/icons";
import { Popover, Space, Tooltip } from "antd";
import { buildRecordDiscussionKey, DATA_RECORD_ANNOTATION_TYPE, emptyRecordDiscussionSummary } from "common/recordDiscussions";
import { useCallback, useMemo, type MouseEvent, type ReactNode } from "react";
import { useSelector } from "react-redux";
import CypressTestIds from "../../../cypress/CypressTestIds";
import RecordDiscussionPreview from "./RecordDiscussionPreview";

const NONE_COLOR = "#aeb0ac";
const COMMENTS_COLOR = "#1677ff";
const ANNOTATION_UNRESOLVED_COLOR = "#d97706";
const ANNOTATION_HIGH_PRIORITY_COLOR = "red";
const ANNOTATION_RESOLVED_COLOR = "#78d11f";

interface IRecordDiscussionIndicatorsProps {
    recordId: string;
    recordType?: string;
    onOpenComments?: () => void;
    onOpenAnnotations?: () => void;
}

const iconStyle = { fontSize: 14 };

const RecordDiscussionIndicators = ({
    recordId,
    recordType = DATA_RECORD_ANNOTATION_TYPE,
    onOpenComments,
    onOpenAnnotations,
}: IRecordDiscussionIndicatorsProps) => {
    const summaryKey = buildRecordDiscussionKey(recordType, recordId);
    const summary = useSelector((state: any) => state.recordDiscussions?.summaryByKey?.[summaryKey]) as IRecordDiscussionSummary | undefined;

    const resolved = summary || emptyRecordDiscussionSummary;
    const { commentsCount, annotationsCount, unresolvedAnnotationsCount, highPriorityOpenCount, closedAnnotationsCount } = resolved;

    const totalAnnotations = (annotationsCount || 0) + (closedAnnotationsCount || 0);

    const annotationColor = useMemo(() => {
        if (totalAnnotations === 0) return NONE_COLOR;
        if (highPriorityOpenCount > 0) return ANNOTATION_HIGH_PRIORITY_COLOR;
        if (unresolvedAnnotationsCount > 0) return ANNOTATION_UNRESOLVED_COLOR;
        return ANNOTATION_RESOLVED_COLOR;
    }, [totalAnnotations, highPriorityOpenCount, unresolvedAnnotationsCount]);

    const annotationTooltip = useMemo(() => {
        if (totalAnnotations === 0) return "No annotations";
        if (highPriorityOpenCount > 0) {
            return `${highPriorityOpenCount} high priority annotation${highPriorityOpenCount === 1 ? "" : "s"} open`;
        }
        if (unresolvedAnnotationsCount > 0) {
            return `${unresolvedAnnotationsCount} unresolved annotation${unresolvedAnnotationsCount === 1 ? "" : "s"}`;
        }
        return `All ${totalAnnotations} annotation${totalAnnotations === 1 ? "" : "s"} resolved`;
    }, [totalAnnotations, highPriorityOpenCount, unresolvedAnnotationsCount]);

    const handleOpen = useCallback((event: MouseEvent, open?: () => void) => {
        if (!open) {
            return;
        }
        event.stopPropagation();
        open();
    }, []);

    const renderIndicator = (options: {
        icon: ReactNode;
        count: number;
        color: string;
        emptyTooltip: string;
        testId: string;
        ariaLabel: string;
        previewKind: "comments" | "annotations";
        previewEntries: any[];
        previewTotal: number;
        previewHasMore?: boolean;
        onOpen?: () => void;
    }) => {
        const interactive = !!options.onOpen;

        const trigger = (
            <span
                role={interactive ? "button" : "img"}
                tabIndex={interactive ? 0 : undefined}
                aria-label={options.ariaLabel}
                data-testid={options.testId}
                data-count={options.count}
                className={`inline-flex items-center gap-0.5 leading-none ${interactive ? "cursor-pointer" : ""}`}
                onClick={(event) => handleOpen(event, options.onOpen)}
                onKeyDown={(event) => {
                    if (interactive && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        event.stopPropagation();
                        options.onOpen!();
                    }
                }}
            >
                {options.icon}
                {options.count > 0 && (
                    <span className="text-[10px] font-semibold leading-none" style={{ color: options.color }}>
                        {options.count}
                    </span>
                )}
            </span>
        );

        if (options.previewTotal === 0) {
            return (
                <Tooltip title={options.onOpen ? `${options.emptyTooltip} — click to add` : options.emptyTooltip} placement="topLeft">
                    {trigger}
                </Tooltip>
            );
        }

        return (
            <Popover
                placement="topLeft"
                trigger={["hover"]}
                mouseEnterDelay={0.3}
                destroyOnHidden
                content={() => (
                    <RecordDiscussionPreview
                        kind={options.previewKind}
                        entries={options.previewEntries}
                        total={options.previewTotal}
                        hasMore={options.previewHasMore}
                        onViewAll={options.onOpen}
                    />
                )}
            >
                {trigger}
            </Popover>
        );
    };

    return (
        <Space size={2}>
            {renderIndicator({
                icon: <MessageTwoTone twoToneColor={commentsCount > 0 ? COMMENTS_COLOR : NONE_COLOR} style={iconStyle} />,
                count: commentsCount || 0,
                color: COMMENTS_COLOR,
                emptyTooltip: "No comments",
                testId: CypressTestIds.DATASET_RECORD_COMMENTS_INDICATOR,
                ariaLabel: commentsCount > 0 ? `${commentsCount} comments on this record` : "No comments on this record",
                previewKind: "comments",
                previewEntries: resolved.commentsPreview || [],
                previewTotal: commentsCount || 0,
                previewHasMore: resolved.hasMoreComments,
                onOpen: onOpenComments,
            })}
            {renderIndicator({
                icon: <FlagTwoTone twoToneColor={annotationColor} style={iconStyle} />,
                count: annotationsCount || 0,
                color: annotationColor,
                emptyTooltip: "No annotations",
                testId: CypressTestIds.DATASET_RECORD_ANNOTATIONS_INDICATOR,
                ariaLabel: annotationTooltip,
                previewKind: "annotations",
                previewEntries: resolved.annotationsPreview || [],
                previewTotal: totalAnnotations,
                previewHasMore: resolved.hasMoreAnnotations,
                onOpen: onOpenAnnotations,
            })}
        </Space>
    );
};

export default RecordDiscussionIndicators;
